import React from "react";
import PropTypes from "prop-types";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import "@inovua/reactdatagrid-community/index.css";
import { EmptyRow } from "../../components";
import { RowClassName } from "../../helpers/RowClassName";
import { addColumnProperty } from "../../helpers/Helper";
import useCellContextCopy from "../../hooks/useCellContextCopy";

const gridStyle = { minHeight: "100%", borderRadius: 4 };

const i18n = Object.assign({}, ReactDataGrid.defaultProps.i18n, {
  pageText: "Страница ",
  ofText: " из ",
  perPageText: "Элементов на странице",
  showingText: "Показано ",
  clearAll: "Очистить все",
  clear: "Очистить",
  showFilteringRow: "Показать фильтры",
  hideFilteringRow: "Скрыть фильтры",
  enable: "Включить",
  disable: "Выключить",
  sortAsc: "По возрастанию",
  sortDesc: "По убыванию",
  unsort: "Без сортировки",
  group: "Group",
  ungroup: "Ungroup",
  lockStart: "Lock start",
  lockEnd: "Lock end",
  unlock: "Unlock",
  columns: "Колонки",
  contains: "Содержит",
  startsWith: "Начинается с",
  endsWith: "Заканчивается на",
  notContains: "Не содержит",
  inlist: "В списке",
  notinlist: "Не в списке",
  neq: "Не эквивалентно",
  inrange: "В диапазоне",
  notinrange: "Вне диапазона",
  eq: "Эквивалентно",
  notEmpty: "Не пусто",
  empty: "Пусто",
  lt: "Меньше чем",
  lte: "Меньше чем или равно",
  gt: "Больше чем",
  gte: "Больше чем или равно",
  before: "До",
  beforeOrOn: "До или на",
  afterOrOn: "После или на",
  after: "После",
  start: "Начало",
  end: "Конец",
  dragHeaderToGroup: "Переместите заголовок для группировки",
  "calendar.todayButtonText": "Сегодня",
  "calendar.clearButtonText": "Очистить",
  "calendar.okButtonText": "OK",
  "calendar.cancelButtonText": "Отмена",
});

const pleaseWait = <b>Идет обновление записей...</b>;

const GridTable = (props) => {
  const {
    items,
    fields,
    loading,
    filter,
    filtering,
    onRowClick,
    onCellEditComplete,
    isDocument = false,
    groups,
    rowStyle,
    columnStyle,
    useRowAlignCenter,
    defaultSortInfo: externalDefaultSortInfo,
    sortInfo,
    onSortInfoChange,
    onColumnToggle,
    onColumnVisibleChange,
    onColumnChange,
    onColumnResize,
    onColumnOrderChange,
    onColumnReorder,
    enableColumnReorder,
    enableColumnVisibilityToggle,
    showColumnMenuTool,
    renderRowContextMenu: externalRenderRowContextMenu,
    ...restProps
  } = props;

  const { handleCellContextMenu } = useCellContextCopy();

  const scrollProps = Object.assign({}, ReactDataGrid.defaultProps.scrollProps, {
    autoHide: false,
    alwaysShowTrack: true,
  });

  const finalDefaultSortInfo = externalDefaultSortInfo !== undefined
    ? externalDefaultSortInfo
    : (isDocument ? { name: 'id', dir: -1 } : null);

  const resolveRowStyle = (rowProps) => {
    const baseStyle = { textAlign: "center" };
    if (typeof rowStyle === "function") {
      return { ...baseStyle, ...rowStyle(rowProps) };
    }
    if (rowStyle) {
      return { ...baseStyle, ...rowStyle };
    }
    return baseStyle;
  };

  // Автоматически прокидываем копирование для всех ячеек, КРОМЕ комментария
  const processedColumns = fields.map(field => {
    const originalRender = field.render;
    const fieldName = field.name;
    const header = field.header;

    return {
      headerAlign: "center",
      ...field,
      render: (renderProps) => {
        const { value, cellProps } = renderProps;

        // Если это колонка comment, не вешаем стандартное копирование по onContextMenu.
        if (fieldName === "comment" || header === "Комментарий") {
          return originalRender ? originalRender(renderProps) : (value || "—");
        }

        // Для всех остальных колонок делаем копирование по правому клику
        return (
          <span
            onContextMenu={(e) => handleCellContextMenu(e, { header: cellProps?.header || field.header, value })}
            style={{
              cursor: 'pointer', display: 'block', width: '100%',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}
          >
            {originalRender ? originalRender(renderProps) : (value !== undefined ? value : "—")}
          </span>
        );
      }
    };
  });

  // Встроенный обработчик контекстного меню строки (срабатывает для комментария и других строк)
  const defaultRenderRowContextMenu = (menuProps, { rowProps, cellProps }) => {
    const { data } = rowProps;
    const columnName = cellProps?.name;
    const header = cellProps?.header;

    // Если передана своя функция renderRowContextMenu снаружи, дадим ей приоритет или скомбинируем
    if (externalRenderRowContextMenu) {
      externalRenderRowContextMenu(menuProps, { rowProps, cellProps });
      if (menuProps.items && menuProps.items.length > 0) return;
    }

    // Логика для комментария как в DOrder
    if (columnName === "comment" || header === "Комментарий") {
      const comment = data?.comment?.trim() || data?.[columnName];

      if (!comment) {
        menuProps.items = [];
        return;
      }

      menuProps.autoDismiss = true;
      menuProps.items = [
        {
          label: `${comment}`,
          disabled: true,
          style: {
            maxWidth: 300,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: "1.4",
            color: "#666",
          },
        },
      ];
    }
  };

  return (
    <ReactDataGrid
      idProperty="id"
      style={props.minimalHeight ? { ...gridStyle, minHeight: props.minimalHeight } : gridStyle}
      i18n={i18n}
      groups={groups}
      showCellBorders={typeof props.showCellBorders === "string" ? props.showCellBorders : true}
      defaultSortInfo={finalDefaultSortInfo}
      sortInfo={sortInfo}
      onSortInfoChange={onSortInfoChange}
      columns={processedColumns}
      dataSource={items}
      rowClassName={RowClassName}
      renderRowContextMenu={defaultRenderRowContextMenu}
      rowHeight={props.rowHeight ? props.rowHeight : 40}
      loading={loading}
      editStartEvent="click"
      loadingText={pleaseWait}
      emptyText={<EmptyRow />}
      pagination={props.pagination !== false ? true : false}
      defaultLimit={100}
      pageSizes={[100, 250, 500, 750, 1000]}
      defaultFilterValue={props.filterValue || props.filter}
      onFilterValueChange={props.onFilterValueChange}
      enableFiltering={filtering ? filtering : false}
      showColumnMenuLockOptions={false}
      showColumnMenuGroupOptions={false}
      showColumnMenuFilterOptions={false}
      showFilteringRow={false}
      showColumnMenuToolOnHover={false}
      scrollProps={scrollProps}
      onCellClick={onRowClick}
      enableKeyboardNavigation={false}
      rowStyle={resolveRowStyle}
      onEditComplete={onCellEditComplete}

      onColumnToggle={onColumnToggle}
      onColumnVisibleChange={onColumnVisibleChange}
      onColumnChange={onColumnChange}
      onColumnResize={onColumnResize}
      onColumnOrderChange={onColumnOrderChange}
      onColumnReorder={onColumnReorder}

      enableColumnReorder={enableColumnReorder}
      enableColumnVisibilityToggle={enableColumnVisibilityToggle}
      showColumnMenuTool={showColumnMenuTool}

      {...restProps}
    />
  );
};

GridTable.propTypes = {
  items: PropTypes.array.isRequired,
  fields: PropTypes.array.isRequired,
};

export default React.memo(GridTable);